import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { FaSpinner } from 'react-icons/fa';
import { FiLink } from 'react-icons/fi';
import api from '../api/api';
import { IGlobalState } from '../model/IGlobalState';

// MCP OAuth consent: Service /authorize redirects here with the client's query string untouched (doc/mcp/spec.md).
// ProtectedRoute sends a signed-out user through the normal login first; Approve gets a code from Service and sends the browser back to the client.
const OAuthAuthorizePage = () => {
    const user = useSelector((state: IGlobalState) => state.user);
    const params = new URLSearchParams(useLocation().search);
    const clientId = params.get('client_id') ?? '';
    const redirectUri = params.get('redirect_uri') ?? '';
    const codeChallenge = params.get('code_challenge') ?? '';
    const state = params.get('state');

    const [ clientName, setClientName ] = useState<string | null>(null);
    const [ error, setError ] = useState('');
    const [ loading, setLoading ] = useState(false);

    useEffect(() => {
        api().getMcpOAuthClient(clientId, redirectUri)
            .then(r => setClientName(r.clientName))
            .catch(() => setError('This connection request is invalid or has expired. Start again from your AI app.'));
    }, [clientId, redirectUri]);

    const approve = async () => {
        setLoading(true);
        try {
            const result = await api().approveMcpOAuth({ clientId, redirectUri, codeChallenge, state });
            window.location.href = result.redirectUrl;
        } catch {
            setError('Could not approve access, please try again');
            setLoading(false);
        }
    };

    // redirect_uri was verified against the registered client by getMcpOAuthClient, so it's safe to send the refusal there
    const deny = () => {
        const url = new URL(redirectUri);
        url.searchParams.set('error', 'access_denied');
        if (state) url.searchParams.set('state', state);
        window.location.href = url.toString();
    };

    return <div className="v2-page oauth-authorize-page">
        <section className="card">
            <span className="icon-tile"><FiLink /></span>
            { error && <>
                <h1>Can't connect</h1>
                <p>{error}</p>
            </> }
            { !error && clientName === null && <FaSpinner className="fa-spin" size={24} color="#0b1739" /> }
            { !error && clientName !== null && <>
                <h1>Connect {clientName}?</h1>
                <p><strong>{clientName}</strong> wants to access Investi as <strong>{user.username}</strong>.</p>
                <p className="note">You can revoke access at any time from Account › MCP Server › Connected Apps.</p>
                <div className="actions">
                    <button type="button" className="btn-outline" onClick={deny} disabled={loading}>Deny</button>
                    <button type="button" className="btn-primary" onClick={approve} disabled={loading}>Approve { loading && <FaSpinner className="fa-spin" size={14} />}</button>
                </div>
            </> }
        </section>
    </div>;
};

export default OAuthAuthorizePage;
